import { Button } from '@assembly-js/design-system';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { STUDIO_INTERNAL_PORTAL_TAB_NAME } from 'src/constants/studioFtux';
import { PortalConfigContext } from 'src/context';
import { useAppDispatch } from 'src/hooks/useStore';
import { setStudioClientPreview } from 'src/store/ui/actions';

import { ChecklistWrapper } from './ChecklistWrapper';
import { useStudioChecklist } from './useStudioChecklist';

interface Props {
  backgroundColor: string;

  sidebarColor: string;
  textColor: string;
}

const hasOpenerTab = (value: unknown): boolean =>
  typeof value === 'object' &&
  value !== null &&
  'closed' in value &&
  value.closed === false;

/**
 * Client-portal variant of the Studio "Getting started" card. Reuses the shared
 * card shell and the studio checklist state; in place of task rows it offers a
 * "Return to CRM" action back to the dashboard, styled with the portal accent.
 */
export const ClientChecklist = ({
  backgroundColor,
  sidebarColor,
  textColor,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { brand, theme } = useContext(PortalConfigContext);
  const { completionPct } = useStudioChecklist();

  // The client "Return to CRM" button uses the portal's configured accent
  // color, falling back to the portal primary when no accent is set.
  const accentColor = brand.clientAccentColor || theme.palette.primary.main;

  const handleReturnToCrm = () => {
    // Returning to the CRM ends the preview, so drop the card for the rest of the
    // session — this clears the flag that gates the client checklist. Done first
    // so the card hides even when there's no live CRM tab to switch back to.
    dispatch(setStudioClientPreview(false));
    // No opener (not launched from the CRM) or a closed opener (the CRM tab was
    // since closed) means there's no live tab to return to — and targeting the
    // name below in that case would spawn a stray blank tab.
    if (!hasOpenerTab(window.opener)) {
      return;
    }
    // opener.focus() alone is a no-op in Chrome (focus-stealing prevention).
    // Re-targeting the Internal Portal tab by the name the CRM page stamped on
    // it switches the browser to that existing tab (empty URL = don't navigate
    // it); focusing the returned handle brings it forward.
    const internalTab = window.open('', STUDIO_INTERNAL_PORTAL_TAB_NAME);
    // If the CRM tab navigated cross-origin after opening this preview, the
    // browser clears its window.name, so the name no longer resolves and the
    // call above spawns a fresh blank tab instead of switching back. The opener
    // is the CRM tab, so when the name still resolves the handle is that same
    // context (reference equality holds even cross-origin — no property read);
    // otherwise close the stray tab so we truly no-op.
    if (internalTab && internalTab !== window.opener) {
      internalTab.close();
      return;
    }
    internalTab?.focus();
  };

  return (
    <ChecklistWrapper
      completionPct={completionPct}
      collapsible={false}
      isClient
      backgroundColor={backgroundColor}
      sidebarColor={sidebarColor}
      textColor={textColor}
    >
      <div className="box-border px-2 pb-2 pt-1">
        <Button
          label={t(
            ($) => $.page_apps.app_builder.getting_started.return_to_crm,
          )}
          variant="primary"
          size="sm"
          className="w-full"
          style={{
            backgroundColor: accentColor,
            borderColor: accentColor,
          }}
          onClick={handleReturnToCrm}
        />
      </div>
    </ChecklistWrapper>
  );
};
